import { Vue, Component, Emit, Watch, Inject } from 'vue-property-decorator';
import { ref, watch } from 'vue';
import { FloorModel, ControlPositionModel, ControlAction, ControlType, AppSettings, IndicatorModel, IndicatorHealth } from '../../types';
import FloorPlan from './floor-plan.vue';
import BuildingEditor from './building-editor.vue';
import ModelStore from '../../model-store';
import Api from '../../api';
import ColorPicker from '../color-picker/ColorPicker.vue';

@Component({
    components: {
        FloorPlan,
        BuildingEditor,
        ColorPicker
    }
})
export default class BuildingComponent extends Vue {

    @Inject() readonly api!: Api;
    @Inject() readonly model!: ModelStore;
    @Inject() readonly appSettings!: AppSettings;

    static readonly SELECTED_FLOOR: string = "selectedFloor";

    localFloors: FloorModel[] = [];
    localControls: ControlPositionModel[] = [];

    selectedFloor: FloorModel | null = null;
    selectedBlindsInfo: ControlPositionModel | null = null;
    selectedGateInfo: ControlPositionModel | null = null;
    selectedColorLight: ControlPositionModel | null = null;
    selectedHsl = {
        hue: 0,
        saturation: 100,
        luminosity: 50,
        alpha: 1
    };
    selectedHslHue: number = 0;
    selectedHslSaturation: number = 0;
    selectedHslLuminosity: number = 0;

    viewMode: BuildingViewMode = BuildingViewMode.Info;

    get isFloorView(): boolean {
        return this.viewMode === BuildingViewMode.Floor;
    }
    get isInfoView(): boolean {
        return this.viewMode === BuildingViewMode.Info;
    }
    get isEditorView(): boolean {
        return this.viewMode === BuildingViewMode.Editor;
    }

    get hasErrors(): boolean {
        return this.model.globalIndicator.health == IndicatorHealth.Bad;
    }
    get hasWarnings(): boolean {
        return this.model.globalIndicator.health == IndicatorHealth.Moderate;
    }

    get blindsValue(): number {
        if (this.selectedBlindsInfo) {
            return this.selectedBlindsInfo.value * 100;
        } else {
            return 0;
        }
    }

    get blindsHeightStyle(): string {
        return `height: ${this.blindsValue}%`;
    }

    @Emit()
    actionExecuted(_controlPosition: ControlPositionModel, _action: ControlAction) {
    }

    @Emit()
    colorSelected(_controlPosition: ControlPositionModel, _color: number) {
    }

    @Emit()
    controlMoved(_floor: FloorModel, _controlPosition: ControlPositionModel) {
    }

    @Emit()
    indicatorDeactivated(indicatorId: string) {
    }

    @Watch("model.floors")
    floorsChanged() {
        this.localFloors = this.model.floors;

        let floor: FloorModel | undefined;
        const floorName = localStorage.getItem(BuildingComponent.SELECTED_FLOOR);
        if (floorName) {
            floor = this.localFloors.filter(f => f.name === floorName)[0];
        }

        if (floor) {
            this.selectFloor(floor);
        }
    }

    mounted() {
        this.floorsChanged();
    }

    selectFloor(floor: FloorModel) {
        this.selectedFloor = floor;
        this.viewMode = BuildingViewMode.Floor;

        if (this.selectedFloor && this.selectedFloor.name) {
            localStorage.setItem(BuildingComponent.SELECTED_FLOOR, this.selectedFloor.name);
        }
    }

    selectInfo() {
        this.selectedFloor = null;
        this.viewMode = BuildingViewMode.Info;
        localStorage.removeItem(BuildingComponent.SELECTED_FLOOR);
    }

    selectEditor() {
        this.selectedFloor = null;
        this.viewMode = BuildingViewMode.Editor;
        localStorage.removeItem(BuildingComponent.SELECTED_FLOOR);
    }

    isSelectedFloor(floor: FloorModel): boolean {
        return this.isFloorView && (this.selectedFloor === floor);
    }

    areLightsOn(floor: FloorModel): boolean {
        return floor.controlPositions.filter(cp => cp.type == ControlType.Light && cp.isUp).length > 0;
    }

    handleControlMoved(controlPosition: ControlPositionModel) {
        if (this.selectedFloor) {
            this.controlMoved(this.selectedFloor, controlPosition);
        }
    }

    handleLightSwitched(control: ControlPositionModel, isOn: boolean) {
        this.actionExecuted(control, isOn ? ControlAction.LightOn : ControlAction.LightOff);
    }

    // Function returning a promise
    fetchData(): Promise<string> {
        return new Promise((resolve, reject) => {
            setTimeout(() => {
                resolve("Async data has been fetched!");
            }, 2000);
        });
    }

    handleColorSelectionRequested(control: ControlPositionModel) {
        if (this.selectedColorLight === null) {
            let h, s, l;
            if (control.value === 0) {
                h = 0;
                s = 1;
                l = 0.5;
            } else {
                // Convert the RGB 24-bit color value into r/g/b 8-bit values
                const num = Math.max(0, Math.min(16777215, Math.round(control.value)));
                const r = (num >> 16) & 255;
                const g = (num >> 8) & 255;
                const b = num & 255;

                // Convert the RGB values to HSL
                [h, s, l] = this.rgb2hsl(r / 255, g / 255, b / 255);
            }

            this.selectedHslHue = h;
            this.selectedHslSaturation = s * 100;
            this.selectedHslLuminosity = l * 100;
            this.selectedHsl = { hue: this.selectedHslHue, saturation: this.selectedHslSaturation, luminosity: this.selectedHslLuminosity, alpha: 1 };

            // Work-around for touch operation. Without this, opening the selector selected a color
            setTimeout(() => this.selectedColorLight = control, 500);
        } else {
            this.selectedColorLight = null;
        }
    }

    @Watch("selectedHslSaturation")
    handleHslSaturationChanged(newHslSaturation: number) {
        this.selectedHsl.saturation = Number(newHslSaturation);
        this.handleColorPicked();
    }

    @Watch("selectedHslLuminosity")
    handleHslLuminosityChanged(newHslLuminosity: number) {
        this.selectedHsl.luminosity = Number(newHslLuminosity);
        this.handleColorPicked();
    }

    handleHslHueChanged(hue: number) {
        this.selectedHslHue = hue;
        this.handleColorPicked();
    }

    handleColorPicked() {
        if (this.selectedColorLight != null) {
            const [r, g, b] = this.hsl2rgb(this.selectedHslHue / 360, this.selectedHslSaturation / 100, this.selectedHslLuminosity / 100);
            const hex = (color: number) => color.toString(16).padStart(2, "0");
            this.colorSelected(this.selectedColorLight, parseInt(`${hex(r)}${hex(g)}${hex(b)}`, 16));
        }
    }

    handleColorOff() {
        if (this.selectedColorLight != null) {
            this.colorSelected(this.selectedColorLight, 0);
            this.selectedColorLight = null;
        }
    }

    handleColorLightCancel() {
        this.selectedColorLight = null;
    }

    handleBlindsMovementRequested(control: ControlPositionModel) {
        if (this.selectedBlindsInfo === null) {
            this.selectedBlindsInfo = control;
        } else {
            this.selectedBlindsInfo = null;
        }
    }

    handleGateMovementRequested(control: ControlPositionModel) {
        if (this.selectedGateInfo === null) {
            this.selectedGateInfo = control;
        } else {
            this.selectedGateInfo = null;
        }
    }

    handleBlindsUp() {
        if (this.selectedBlindsInfo != null) {
            this.actionExecuted(this.selectedBlindsInfo, ControlAction.BlindsUp);
            this.selectedBlindsInfo = null;
        }
    }

    handleBlindsStepUp() {
        if (this.selectedBlindsInfo != null) {
            this.actionExecuted(this.selectedBlindsInfo, ControlAction.BlindsStepUp);
            this.selectedBlindsInfo = null;
        }
    }

    handleBlindsStepDown() {
        if (this.selectedBlindsInfo != null) {
            this.actionExecuted(this.selectedBlindsInfo, ControlAction.BlindsStepDown);
            this.selectedBlindsInfo = null;
        }
    }

    handleBlindsDown() {
        if (this.selectedBlindsInfo != null) {
            this.actionExecuted(this.selectedBlindsInfo, ControlAction.BlindsDown);
            this.selectedBlindsInfo = null;
        }
    }

    handleBlindsCancel() {
        this.selectedBlindsInfo = null;
    }

    handleGateOpen() {
        if (this.selectedGateInfo != null) {
            this.actionExecuted(this.selectedGateInfo, ControlAction.GateOpen);
            this.selectedGateInfo = null;
        }
    }

    handleGateClose() {
        if (this.selectedGateInfo != null) {
            this.actionExecuted(this.selectedGateInfo, ControlAction.GateClose);
            this.selectedGateInfo = null;
        }
    }

    handleGateCancel() {
        this.selectedGateInfo = null;
    }

    deactivateIndicator(indicator: IndicatorModel) {
        this.indicatorDeactivated(indicator.id);
    }

    static numberToColorString(num: number): string {
        // Ensure the number is within the range of 0 to 16777215 (0xFFFFFF)
        const clampedNum = Math.max(0, Math.min(16777215, num));
        // Convert the number to a hexadecimal string and pad with leading zeros if necessary
        const hexString = clampedNum.toString(16).padStart(6, '0');
        // Return the color string in the format #RRGGBB
        return `#${hexString}`;
    }

    rgb2hsl(r: number, g: number, b: number) {
        let v = Math.max(r, g, b), c = v - Math.min(r, g, b), f = (1 - Math.abs(v + v - c - 1));
        let h = c && ((v == r) ? (g - b) / c : ((v == g) ? 2 + (b - r) / c : 4 + (r - g) / c));
        return [60 * (h < 0 ? h + 6 : h), f ? c / f : 0, (v + v - c) / 2];
    }

    /**
     * Converts an HSL color value to RGB. Conversion formula
     * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
     * Assumes h, s, and l are contained in the set [0, 1] and
     * returns r, g, and b in the set [0, 255].
     *
     * @param   {number}  h       The hue
     * @param   {number}  s       The saturation
     * @param   {number}  l       The lightness
     * @return  {Array}           The RGB representation
     */
    hsl2rgb(h: number, s: number, l: number): number[] {
        let r, g, b;

        if (s == 0) {
            r = g = b = l; // achromatic
        } else {
            const hue2rgb = function hue2rgb(p: number, q: number, t: number) {
                if (t < 0) t += 1;
                if (t > 1) t -= 1;
                if (t < 1 / 6) return p + (q - p) * 6 * t;
                if (t < 1 / 2) return q;
                if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
                return p;
            }

            const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
            const p = 2 * l - q;
            r = hue2rgb(p, q, h + 1 / 3);
            g = hue2rgb(p, q, h);
            b = hue2rgb(p, q, h - 1 / 3);
        }

        return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    }
}

enum BuildingViewMode {
    Floor,
    Info,
    Editor
}
